import { motion } from "framer-motion";
import { Cpu, BrainCircuit, Atom } from "lucide-react";

const ENGINES = [
  {
    n: "01",
    icon: Cpu,
    title: "THE KERNEL",
    tag: "THE REFEREE",
    desc: "A plain program written in Python keeps the rules of the world: where people are, what things cost, who is sick, what time it is. The models may talk; the referee decides what's real.",
    points: [
      "Python 3, standard library only, no frameworks",
      "Every moment is written to a tamper-proof ledger",
      "Any day can be re-run exactly, or forked to test \u201cwhat if\u201d",
    ],
  },
  {
    n: "02",
    icon: BrainCircuit,
    title: "THE MINDS",
    tag: "WORDS + CHOICES",
    desc: "When residents talk or face a real decision, two models help. Qwen 2.5 7B writes what people say, each in their own voice, from their own memories. TypeSafe Jev 1.13 weighs what they do next. Neither can break the world.",
    points: [
      "Qwen 2.5 7B: speech, diaries, the newspaper",
      "TypeSafe Jev 1.13: probabilities, not commands",
      "Every word is validated before it counts",
    ],
  },
  {
    n: "03",
    icon: Atom,
    title: "THE QUANTUM TWIST",
    tag: "HOW BELIEFS BEND",
    desc: "How residents update their beliefs is itself an experiment. In the quantum-probability model, a belief is a blend of possibilities and new evidence rotates it, so the order you hear things in changes what you end up believing, echoing how real minds work. It runs on ordinary computers.",
    points: [
      "Beliefs carry their receipts: saw it, was told, hearsay",
      "Heard and seen evidence can interfere",
      "A declared setting of the city, not hidden math",
    ],
  },
];

export default function TechStack() {
  return (
    <section id="tech" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12"
        >
          <div>
            <p className="text-gb-neon text-[11px] tracking-[0.3em] mb-3">07 · HOW IT WORKS</p>
            <h2 className="font-title text-6xl sm:text-7xl lg:text-8xl uppercase text-gb-pale leading-[0.85]">
              Three engines,
              <br />
              <span className="text-outline">one town</span>
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-gb-light/90">
            What actually makes the simulated city tick, in plain words.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-4">
          {ENGINES.map((e, i) => {
            const Icon = e.icon;
            return (
              <motion.div
                key={e.n}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="group bg-gb-card border-2 border-gb-line hover:border-gb-accent/60 p-7 h-full flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_40px_rgba(64,240,112,0.08)]"
                data-testid={`tech-engine-${i + 1}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="inline-flex items-center justify-center w-12 h-12 border-2 border-gb-accent/50 text-gb-neon group-hover:bg-gb-neon group-hover:text-gb-bg transition-colors duration-300">
                    <Icon size={22} />
                  </span>
                  <span className="font-pixel text-2xl text-gb-accent/50">{e.n}</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap mb-3">
                  <h3 className="font-title text-3xl text-gb-pale tracking-wide">{e.title}</h3>
                  <span className="font-arcade text-[6px] text-gb-accent border border-gb-accent/40 px-2 py-1.5">
                    {e.tag}
                  </span>
                </div>
                <p className="text-[13px] leading-relaxed text-gb-light/90">{e.desc}</p>
                <ul className="mt-auto pt-5 space-y-2">
                  {e.points.map((p) => (
                    <li key={p} className="text-[12px] leading-relaxed text-gb-light/80 flex gap-2">
                      <span className="text-gb-neon shrink-0">▸</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-8 text-[10px] sm:text-[11px] tracking-[0.25em] text-gb-muted text-center"
        >
        </motion.p>
      </div>
    </section>
  );
}
